import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Checkbox, Header, Icon } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import {
    ClippedContent,
    ControlledPopup,
    DateTimeUtils,
    OnHoverOnClick,
    Person,
    Table,
    USER_PERMISSIONS,
} from "dyl-components";
import { PhoneUtil, StringUtils } from "utils";
import HoverContent from "shared/hoverContent";
import Contact from "shared/Contact";
import { ActivityPopup } from "shared/forms/NewActivity";
import RestrictedView from "shared/RestrictedView";
import TextIcon from "shared/TextIcon";

const getIconClassName = (action, disabled) => {
    const actionText = action.split(" ")[0];
    switch (actionText) {
        case "Call":
            return "fa-solid fa-phone";
        case "Email":
            return "fa-solid fa-envelope";
        case "Text":
            return "fa-solid fa-comments";
        default:
            return "";
    }
};

const Row = ({
    sequenceTask,
    onSequenceTaskReschedule,
    onTaskComplete,
    onTaskRemove,
    getEmailTaskContent,
    hasText,
    onTaskConfirm
}) => {
    const {
        ts: created,
        id,
        user,
        workflow,
        status,
        action,
        contact,
    } = sequenceTask;

    const dispatch = useDispatch();
    const isComplete = sequenceTask.status?.toLowerCase()?.includes("complete");
    const isCancelled = sequenceTask.status
        ?.toLowerCase()
        ?.includes("canceled");
    const userPhoneCreds = useSelector((state) => state?.officeView?.creds);
    const [isActivityFormOpen, setIsActivityFormOpen] = useState(false);
    const [isTextActivityFormOpen, setIsTextActivityFormOpen] = useState(false);
    const { pathname } = useLocation();
    const isSettings = pathname.split("/")[1] === "settings";
    const isPhoneSystemSupported =
        PhoneUtil.isPhoneSystemSupported(userPhoneCreds);
    const isUnsubscribedFromTexting = useSelector((state) => state.text.unsubscribedPhones?.includes(contact?.phone?.phone));

    const onSequenceCall = (sequence) => {
        const { id: contact_id, phone: destination } = sequence?.contact || {};
        if (destination) {
            PhoneUtil.C2D({ payload: { contact_id, destination, requestor_id: sequence?.id, call_type: sequence.action_data.callback ? "callback" : "sequence" }, dispatch });
        }
    }

    const getActionButton = (action) => {
        if (action && action !== "To-Do") {
            if (action === "Email") {
                return getEmailTaskContent(sequenceTask, isCancelled);
            }
            if (action === "Call" && !isPhoneSystemSupported) {
                return (
                    <ActivityPopup
                        trigger={
                            <Icon
                                size="large"
                                link
                                className={`${getIconClassName(action)}`}
                                color="primary"
                                style={{ marginTop: 2 }}
                                disabled={!contact?.phone?.phone}
                            />
                        }
                        activityProps={{
                            hideActivitySelection: true,
                            contact: { id: contact?.id },
                            isContact: true
                        }}
                        open={isActivityFormOpen}
                        onOpen={() => setIsActivityFormOpen(true)}
                        onClose={() => setIsActivityFormOpen(false)}
                        position="bottom right"
                    />
                );
            }
            if (action === "Text") {
                return (!hasText ? 
                    <TextIcon 
                        account_id={contact?.account_id}
                        contact_id={contact?.id}
                        isActivityFormOpen={isActivityFormOpen}
                        disabled={isComplete || !contact?.phone?.phone || isCancelled}
                        onOpenActivityForm={() => {setIsActivityFormOpen(true)}}
                        onCloseActivityForm={() => {setIsActivityFormOpen(false)}}
                        primary
                        size='large'
                    />
                : <ControlledPopup
                    header={
                        <Header as='h4' textAlign='center'>
                            Text will be sent to recipient.
                        </Header>
                    }
                    trigger={<span>
                        <TextIcon 
                            account_id={contact?.account_id}
                            contact_id={contact?.id}
                            phone={contact?.phone?.phone}
                            primary
                            size='large'
                            disabled={isComplete || !contact?.phone?.phone || isCancelled}
                            isActivityFormOpen={isTextActivityFormOpen}
                            onOpenActivityForm={() => setIsTextActivityFormOpen(true)}
                            onCloseActivityForm={() => setIsTextActivityFormOpen(false)}
                        />
                    </span>}
                    onConfirm={() => {
                        onTaskConfirm(id)
                    }}
                    inverted
                    position="bottom center"
                    disabled={isComplete || !contact?.phone?.phone || isUnsubscribedFromTexting || !hasText || isCancelled}
                />)
            }
            return (
                <Icon
                    size="large"
                    link
                    className={`${getIconClassName(action)}`}
                    color="primary"
                    style={{ marginTop: 2 }}
                    disabled={isCancelled || !!!sequenceTask?.contact?.phone?.phone || sequenceTask?.contact?.call_suspended || sequenceTask?.contact?.phone?.call_suspended}
                    onClick={() => onSequenceCall(sequenceTask)}
                />
            );
        }
    };

    return (
        <Table.Row>
            <Table.Cell className="Table__MediumFixedWidth">
                <div>
                    <b>
                        {DateTimeUtils.formatEpoch(
                            created,
                            DateTimeUtils.WORD_DATE_FORMAT
                        )}
                    </b>
                </div>
                <div>
                    {DateTimeUtils.formatEpoch(
                        created,
                        DateTimeUtils.TIME_FORMAT
                    )}
                </div>
            </Table.Cell>
            <Table.Cell className="Table__SmallMediumFixedWidth">
                {user ? 
                    <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={user}
                            />
                        }
                        clickable={false}
                        trigger={
                            <div className="Table__SmallMediumFixedWidth" >

                                <Person
                                    username={`${user?.first_name} ${user?.last_name}`}
                                    isInactiveUser={!user?.active}
                                    popup={false}
                                    email={user?.email}
                                />
                            </div>
                        }
                    /> : "Unassigned"
                }
            </Table.Cell>
            <Table.Cell className={`Table__SmallFixedWidth SequenceReports__StatusColumn--${status}`}>
                <b>{StringUtils.capitalize(status)}</b>
            </Table.Cell>
            <Table.Cell className="Table__MediumFixedWidth">
                <div className="SequenceReports__NameColumn">
                    {!workflow.deleted ? (
                        <Link to={`/settings/sequences/${workflow.id}`}>
                            <ClippedContent>{workflow.name}</ClippedContent>
                        </Link>
                    ) : (
                        workflow.name
                    )}
                </div>
            </Table.Cell>
            <Table.Cell className="Table__SmallFixedWidth">{action}</Table.Cell>
            <Table.Cell className="Table__MediumFixedWidth SequenceReports__ContactNameColumn">
                {contact && (
                    <Contact
                        contact={{
                            ...contact,
                            email: {
                                id: contact?.email?.email,
                                email: contact?.email?.email,
                            },
                            phone: {
                                id: contact?.phone?.phone,
                                phone: contact?.phone?.phone,
                            },
                        }}
                        name={`${contact?.first_name} ${contact?.last_name}`}
                        linkToContact={
                            <Link
                                to={`/contact/${contact?.id}`}
                            >{`${contact?.first_name} ${contact?.last_name}`}</Link>
                        }
                        hideId={false}
                        isSimplified
                        isCompact
                    />
                )}
            </Table.Cell>
            <Table.Cell className="Table__MediumFixedWidth">
                {contact && (
                    <div className={isSettings ? "SequenceReports__AccountNameColumn" : ""}>
                        <ClippedContent popupContent={contact.account_name}>
                            {contact.account_type &&
                                (contact.account_type === "business" ? (
                                    <Icon className="fa-solid fa-building" />
                                ) : (
                                    <Icon className="fa-solid fa-house" />
                                ))}
                            <Link to={`/account/${contact.account_id}`}>
                                {contact.account_name}
                            </Link>
                        </ClippedContent>
                    </div>
                )}
            </Table.Cell>
            <Table.Cell className="Table__MediumFixedWidth">
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 10,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <RestrictedView type='disabled' userPermission={USER_PERMISSIONS.SEQUENCE_REPORT_EDIT}>
                        <Checkbox
                            className="SequenceTime__taskCheckbox"
                            style={{ marginRight: 6 }}
                            checked={isComplete}
                            disabled={isComplete || isCancelled}
                            onClick={() => {
                                if (!isComplete && !isCancelled) {
                                    onTaskComplete(id, action);
                                }
                            }}
                        />
                    </RestrictedView>
                    <RestrictedView type='disabled' userPermission={USER_PERMISSIONS.SEQUENCE_REPORT_EDIT}>
                        {getActionButton(action)}
                    </RestrictedView>
                    <RestrictedView type='disabled' userPermission={USER_PERMISSIONS.SEQUENCE_REPORT_EDIT}>
                        <Icon
                            size="large"
                            link
                            className="fa-solid fa-calendar"
                            color="primary"
                            style={{ marginTop: 2 }}
                            onClick={() => onSequenceTaskReschedule(id)}
                            disabled={isComplete || isCancelled}
                        />
                    </RestrictedView>
                    <RestrictedView type='controlledPopupDisabled' userPermission={USER_PERMISSIONS.SEQUENCE_REPORT_DELETE}>
                        <ControlledPopup
                            header={
                                <Header as="h4" textAlign="center">
                                    Are you sure?
                                </Header>
                            }
                            trigger={
                                <Icon
                                    size="big"
                                    link
                                    className="fa-solid fa-xmark redIcon"
                                    disabled={isComplete || isCancelled}
                                />
                            }
                            onConfirm={() => onTaskRemove(id)}
                            disabled={isComplete || isCancelled}
                            position={"top center"}
                        />
                    </RestrictedView>
                </div>
            </Table.Cell>
        </Table.Row>
    );
};

export default Row;
